import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  ChevronRight, 
  ChevronDown,
  Folder,
  FileText,
  Clock,
  Star,
  Download,
  ArrowLeft,
  Home
} from 'lucide-react';
import { 
  backendData, 
  frontendData, 
  mobileData, 
  qaData, 
  pmData, 
  ceoData 
} from '../mocks';
import ResourceDetail from './ResourceDetail';

const Resources = ({ user }) => {
  const { resourceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [treeWidth, setTreeWidth] = useState(30); // Porcentaje del ancho

  // Obtener todos los datos
  const allData = [
    backendData,
    frontendData,
    mobileData,
    qaData,
    pmData,
    ceoData
  ];

  // Función para obtener el ícono por nombre
  const getIconByName = (iconName) => {
    const iconMap = {
      'Code': '💻',
      'Server': '🖥️',
      'Globe': '🌐',
      'Shield': '🛡️',
      'Users': '👥',
      'BarChart3': '📊',
      'BookOpen': '📚',
      'Play': '▶️',
      'Target': '🎯',
      'Award': '🏆',
      'Smartphone': '📱',
      'Building': '🏗️',
      'Palette': '🎨',
      'Lightbulb': '💡',
      'FileText': '📄',
      'Database': '🗄️',
      'Zap': '⚡',
      'Wrench': '🔧'
    };
    return iconMap[iconName] || '📄';
  };

  // Función para buscar un recurso por ID
  const findResourceById = (id) => {
    for (const roleData of allData) {
      for (const category of roleData.categories) {
        for (const resource of category.resources) {
          if (resource.id === id) {
            return { resource, category, role: roleData };
          }
        }
      }
    }
    return null;
  };

  // Función para obtener breadcrumbs
  const getBreadcrumbs = (resourceId) => {
    if (!resourceId) return [];
    
    const found = findResourceById(resourceId);
    if (!found) return [];
    
    return [
      { name: found.role.title, path: `/resources` },
      { name: found.category.name, path: `/resources?category=${found.category.name}` },
      { name: found.resource.title, path: `/resources/${resourceId}` }
    ];
  };

  // Efecto para manejar cambios en la URL
  useEffect(() => {
    if (resourceId) {
      const found = findResourceById(resourceId);
      if (found) {
        setSelectedResource(found.resource);
        setSelectedCategory(found.category);
        // Expandir la categoría correspondiente
        setExpandedCategories(prev => new Set([...prev, found.category.name]));
      }
    } else {
      setSelectedResource(null);
    }
  }, [resourceId]);

  // Función para expandir/contraer categoría
  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  // Función para manejar clic en categoría
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setSelectedResource(null);
    navigate(`/resources?category=${category.name}`);
  };

  // Función para manejar clic en recurso
  const handleResourceClick = (resource) => {
    setSelectedResource(resource);
    navigate(`/resources/${resource.id}`);
  };

  // Función para manejar clic en home
  const handleHomeClick = () => {
    setSelectedCategory(null);
    setSelectedResource(null);
    navigate('/resources');
  };

  // Función para manejar clic en back
  const handleBackClick = () => {
    if (selectedResource) {
      setSelectedResource(null);
      navigate('/resources');
    } else if (selectedCategory) {
      setSelectedCategory(null);
      navigate('/resources');
    }
  };

  const breadcrumbs = getBreadcrumbs(resourceId);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="page-header border-b border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleHomeClick}
              className="logo-container flex items-center space-x-2 text-text hover:text-primary transition-colors"
            >
              <Home className="w-5 h-5" />
              <span>Innova Hub</span>
            </button>
            
            {breadcrumbs.length > 0 && (
              <div className="flex items-center space-x-2 text-text-secondary">
                {breadcrumbs.map((crumb, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <span>/</span>
                    <button
                      onClick={() => navigate(crumb.path)}
                      className="breadcrumb-item hover:text-primary transition-colors"
                    >
                      {crumb.name}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center space-x-4">    
            {(selectedCategory || selectedResource) && (
              <button
                onClick={handleBackClick}
                className="back-button flex items-center space-x-2 px-3 py-1 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Atrás</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-80px)]">
        {/* Panel izquierdo - Árbol de recursos */}
        <div 
          className="left-panel custom-scrollbar overflow-y-auto"
          style={{ width: `${treeWidth}%` }}
        >
          <div className="p-4">
            <h2 className="text-lg font-semibold text-text mb-4">Recursos Disponibles</h2>
            
            <div className="space-y-2">
              {allData.map((roleData) => (
                <div key={roleData.id} className="space-y-2">
                  {/* Role header */}
                  <div className="tree-node flex items-center space-x-2 text-text font-medium p-2 rounded-lg">
                    <span>{getIconByName(roleData.icon)}</span>
                    <span>{roleData.title}</span>
                  </div>
                  
                  {/* Categories */}
                  <div className="ml-4 space-y-1">
                    {roleData.categories.map((category) => (
                      <div key={category.name}>
                        {/* Category header */}
                        <button
                          onClick={() => toggleCategory(category.name)}
                          className="tree-category flex items-center space-x-2 w-full text-left p-2 rounded-lg hover:bg-gray-700 transition-colors"
                        >
                          {expandedCategories.has(category.name) ? (
                            <ChevronDown className="status-icon expanded w-4 h-4 text-text-secondary" />
                          ) : (
                            <ChevronRight className="status-icon w-4 h-4 text-text-secondary" />
                          )}
                          <span className="text-text-secondary">{category.name}</span>
                          <span className="resource-count ml-auto">
                            {category.resources.length}
                          </span>
                        </button>
                        
                        {/* Resources */}
                        <AnimatePresence>
                          {expandedCategories.has(category.name) && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="ml-6 space-y-1 expandable-tree expanded"
                            >
                              {category.resources.map((resource) => (
                                <button
                                  key={resource.id}
                                  onClick={() => handleResourceClick(resource)}
                                  className={`tree-resource flex items-center space-x-2 w-full text-left p-2 rounded-lg text-sm transition-colors ${
                                    selectedResource?.id === resource.id
                                      ? 'selected'
                                      : 'hover:bg-gray-700 text-text-secondary'
                                  }`}
                                >
                                  <FileText className="w-4 h-4" />
                                  <span className="truncate">{resource.title}</span>
                                </button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Panel derecho - Contenido */}
        <div className="right-panel flex-1 overflow-y-auto custom-scrollbar">
          <AnimatePresence mode="wait">
            {selectedResource ? (
              // Vista del recurso - Ahora inline, no modal
              <motion.div
                key="resource"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="h-full p-6"
              >
                <ResourceDetail
                  resource={selectedResource}
                  onClose={() => handleBackClick()}
                />
              </motion.div>
            ) : selectedCategory ? (
              // Vista de la categoría
              <motion.div
                key="category"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-6"
              >
                <div className="mb-6">
                  <h1 className="text-3xl font-bold text-text mb-2">{selectedCategory.name}</h1>
                  <p className="text-text-secondary">
                    {selectedCategory.resources.length} recursos disponibles
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {selectedCategory.resources.map((resource, index) => (
                    <motion.div
                      key={resource.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="resource-card bg-surface rounded-lg p-6 cursor-pointer"
                      onClick={() => handleResourceClick(resource)}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <h3 className="text-xl font-semibold text-text">{resource.title}</h3>
                        <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm">
                          {resource.type}
                        </span>
                      </div>
                      
                      <p className="text-text-secondary mb-4 line-clamp-2">{resource.description}</p>
                      
                      <div className="flex items-center justify-between text-sm text-text-secondary">
                        <div className="flex items-center space-x-4">
                          <span className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{resource.estimatedTime}</span>
                          </span>
                          <span className="px-2 py-1 bg-gray-800 rounded-full">
                            {resource.difficulty}
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <span className="flex items-center space-x-1">
                            <Star className="w-4 h-4 text-yellow-500 fill-current" />
                            <span>{resource.rating}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Download className="w-4 h-4" />
                            <span>{resource.downloads}</span>
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ) : (
              // Vista principal - Todos los roles
              <motion.div
                key="main"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-6"
              >
                <div className="mb-8">
                  <h1 className="text-3xl font-bold text-text mb-2">Explorar Recursos</h1>
                  <p className="text-text-secondary">
                    Navega por todas las categorías y recursos disponibles en Innova Hub
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {allData.map((roleData) => (
                    <motion.div
                      key={roleData.id}
                      whileHover={{ scale: 1.02 }}
                      className="resource-card bg-surface rounded-lg p-6 cursor-pointer"
                      onClick={() => handleCategoryClick(roleData.categories[0])}
                    >
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="text-3xl">{getIconByName(roleData.icon)}</div>
                        <div>
                          <h3 className="text-xl font-semibold text-text">{roleData.title}</h3>
                          <p className="text-text-secondary text-sm">{roleData.description}</p>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-text-secondary">Categorías:</span>
                          <span className="text-text">{roleData.categories.length}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-text-secondary">Recursos:</span>
                          <span className="text-text">
                            {roleData.categories.reduce((total, cat) => total + cat.resources.length, 0)}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Resources;
